// Which page sent a business request, and for which business (PRD 89 §4.9).
//
// A refusal can arrive after the person has moved on. A dispatcher lands on
// the dashboard, which they may not read; before its requests come back they
// tap Customers, which they may. The dashboard's 403 `role_forbidden` then
// answers while Customers is on screen — and if the shell blanked whatever
// page was showing when a refusal arrived, it would blank the one page that
// was allowed, and a second tap on Customers (the same path) would not clear
// it. So businessApiFetch notes the page and business at SEND time, and
// parseEnvelope reports them with the refusal: the shell marks the page that
// asked, not the page that happens to be open.
//
// A WeakMap keyed by the Response, so nothing outlives the response it
// describes and no service signature has to change.

export interface BusinessRequestOrigin {
  /** `window.location.pathname` when the request was sent. */
  path: string;
  /** The X-Business-Id it was sent with. */
  businessId: string | null;
}

const origins = new WeakMap<object, BusinessRequestOrigin>();

/** The page the browser is on now, or '' where there is no window. */
export const currentPagePath = (): string => {
  try {
    return window.location.pathname;
  } catch {
    return '';
  }
};

/**
 * Note where `response` was asked for. Ignores anything that is not an object
 * — a test double of apiFetch may resolve to nothing at all.
 */
export const recordRequestOrigin = (response: unknown, origin: BusinessRequestOrigin): void => {
  if (response !== null && typeof response === 'object') origins.set(response, origin);
};

/** Where `response` was asked for, or null when it was not recorded. */
export const requestOriginOf = (response: unknown): BusinessRequestOrigin | null =>
  response !== null && typeof response === 'object' ? origins.get(response) ?? null : null;
